import { useTranslations } from "next-intl";
import { DataField, Stack } from "@leabankab/component-library";
import { SummarySection } from "@/components/ui";
import { useCurrency } from "@/hooks";
import { formatRate } from "@/shared/amount";
import { useConstantOptions } from "../../customer-information/hooks/use-constant-options";
import { CreditCardSummaryPageContext, KycAnswers } from "../types";

const CreditCardSummaryComponent = ({
	creditLimit,
	nominalInterestRate,
	kycQuestions,
	isPep,
	citizenship,
}: CreditCardSummaryPageContext) => {
	const t = useTranslations("pages.SummaryPage");
	const tConstants = useTranslations("constants");
	const tCommon = useTranslations("common");
	const countryOptions = useConstantOptions("countries");
	const { format } = useCurrency();

	const answers = kycQuestions.reduce<Record<string, unknown>>(
		(acc, curr) => ({ ...acc, ...curr }),
		{}
	) as KycAnswers;

	const yesNo = (bool: boolean): string => tCommon(bool ? "yes" : "no");

	const contentAbout = [
		{
			description: t("credit.about.content.creditLimit"),
			value: format(creditLimit),
		},
		nominalInterestRate != null
			? {
					description: t("credit.about.content.nominalInterestRate"),
					value: formatRate(nominalInterestRate),
				}
			: undefined,
	].filter(Boolean) as { description: string; value: string }[];

	const contentCustomer = [
		{
			description: t("common.pep"),
			value: yesNo(isPep),
		},
		citizenship !== undefined
			? {
					description: t("common.citizenship"),
					value: citizenship.map((value) => (
						<div key={value}>{countryOptions[value] ?? value}</div>
					)),
				}
			: undefined,
		answers.expectedUseOfCard !== undefined
			? {
					description: t("credit.customer.content.expectedUseOfCard"),
					value: answers.expectedUseOfCard.map((value) => (
						<div key={value}>
							{tConstants(`expectedUseOfCardOptions.options.${value}`)}
						</div>
					)),
				}
			: undefined,
		answers.expectedLocationUseOfCard !== undefined
			? {
					description: t("credit.customer.content.expectedLocationUseOfCard"),
					value: answers.expectedLocationUseOfCard.map((value) => (
						<div key={value}>
							{tConstants(`expectedLocationUseOfCardOptions.options.${value}`)}
						</div>
					)),
				}
			: undefined,
		answers.useCardForTransactions !== undefined
			? {
					description: t("credit.customer.content.useCardForTransactions"),
					value: yesNo(answers.useCardForTransactions),
				}
			: undefined,
		answers.useCardForTransactionsLocation !== undefined
			? {
					description: t(
						"credit.customer.content.useCardForTransactionsLocation"
					),
					value: answers.useCardForTransactionsLocation.map((value) => (
						<div key={value}>
							{tConstants(`expectedLocationUseOfCardOptions.options.${value}`)}
						</div>
					)),
				}
			: undefined,
		answers.withdrawMoreThan50k !== undefined
			? {
					description: t("credit.customer.content.withdrawMoreThan50k"),
					value: yesNo(answers.withdrawMoreThan50k),
				}
			: undefined,
		answers.howToPayForInvoices !== undefined
			? {
					description: t("credit.customer.content.howToPayForInvoices"),
					value: answers.howToPayForInvoices.map((value) => (
						<div key={value}>
							{tConstants(`howToPayForInvoicesOptions.options.${value}`)}
						</div>
					)),
				}
			: undefined,
	].filter(Boolean) as { description: string; value: string }[];

	return (
		<Stack className="gap-1">
			<SummarySection title={t("credit.about.header")} defaultExpanded>
				<Stack>
					{contentAbout.map((item, index) => (
						<DataField
							key={item.description}
							label={item.description}
							value={item.value}
							divider={index !== contentAbout.length - 1}
						/>
					))}
				</Stack>
			</SummarySection>

			<SummarySection title={t("credit.customer.header")} defaultExpanded>
				<Stack>
					{contentCustomer.map((item, index) => (
						<DataField
							key={item.description}
							label={item.description}
							value={item.value}
							divider={index !== contentCustomer.length - 1}
						/>
					))}
				</Stack>
			</SummarySection>
		</Stack>
	);
};

export default CreditCardSummaryComponent;
